import React from 'react';
import './gpj-pathways.css';

const PATHWAYS = [
  {
    slug: 'served',
    title: "I've been served with court papers",
    desc: 'Someone has started a case against you. You have a limited time to respond, and not responding can mean a decision is made without you.',
    steps: ['Understand what you were served', 'Prepare your Answer', 'Serve and file on time']
  },
  {
    slug: 'start',
    title: 'I need to start a case',
    desc: 'You are asking the court to decide on decision-making responsibility, parenting time, or support for the first time.',
    steps: ['Work out which court and which forms', 'Prepare your Application', 'Serve, file, and set your first date']
  },
  {
    slug: 'court-date',
    title: 'I have a court date coming up',
    desc: 'A case conference, settlement conference, or motion is scheduled. Each one needs different material, filed by a different deadline.',
    steps: ['Identify which kind of date it is', 'Prepare the right brief or affidavit', 'File by the deadline and prepare to attend']
  },
  {
    slug: 'change-order',
    title: 'I need to change an existing order',
    desc: 'Something has changed since your order was made, and you need parenting time, decision-making, or support changed to match.',
    steps: ['Check whether your change qualifies', 'Prepare a motion to change', 'Serve, file, and prepare your evidence']
  },
  {
    slug: 'enforcement',
    title: "The other parent isn't following the order",
    desc: 'An order is in place but it is not being obeyed. There is a process for bringing that back before the court.',
    steps: ['Record what has happened', 'Choose the right enforcement route', 'Prepare and file your material']
  },
  {
    slug: 'appeal',
    title: 'I want to appeal a decision',
    desc: 'Appeal routes and time rules depend on several details. This pathway links each general rule to its condition and official source; it does not calculate a route or date.',
    steps: ['Review general route factors', 'Read condition-bound time rules', 'Open verified official sources'],
    flag: 'Only on GPJ',
    view: 'appeals'
  }
];

function PathwayCard({ pathway, index, setCurrentView }) {
  const content = (
    <>
      {pathway.flag && <span className="gpj-path__badge">{pathway.flag}</span>}
      <span className="gpj-path__num" aria-hidden="true">{index + 1}</span>
      <h3>{pathway.title}</h3>
      <p className="gpj-path__desc">{pathway.desc}</p>
      <ul className="gpj-path__steps">
        {pathway.steps.map((step) => <li key={step}>{step}</li>)}
      </ul>
      <span className={`gpj-path__go${pathway.view ? '' : ' gpj-path__go--muted'}`}>
        {pathway.view ? 'Start this pathway' : 'Interactive pathway coming soon'}
        {pathway.view && <span className="arrow" aria-hidden="true">→</span>}
      </span>
    </>
  );

  const className = `gpj-path${pathway.flag ? ' gpj-path--flag' : ''}${pathway.view ? '' : ' gpj-path--unavailable'}`;

  if (pathway.view) {
    return (
      <button type="button" className={className} onClick={() => setCurrentView(pathway.view)}>
        {content}
      </button>
    );
  }

  return <article className={className}>{content}</article>;
}

export default function GuidedPathways({ setCurrentView }) {
  return (
    <div className="gpj">
      <section className="gpj-section">
        <div className="gpj-wrap">
          <div className="gpj-sec-head">
            <span className="gpj-eyebrow">Guided pathways</span>
            <h2>Where are you in your case?</h2>
            <p>
              Choose the situation that matches yours. Each pathway walks you through what
              happens next and points to relevant forms and official sources.
            </p>
          </div>

          <div className="gpj-paths">
            {PATHWAYS.map((pathway, index) => (
              <PathwayCard
                key={pathway.slug}
                pathway={pathway}
                index={index}
                setCurrentView={setCurrentView}
              />
            ))}
          </div>

          <p className="gpj-browse-note">
            Not sure which fits? <strong>The three-question pathway matcher is coming soon.</strong>
          </p>
        </div>
      </section>

      <section className="gpj-section gpj-band">
        <div className="gpj-wrap">
          <div className="gpj-sec-head">
            <span className="gpj-eyebrow">Reference</span>
            <h2>Forms and law, in one place</h2>
            <p>
              Every pathway links to these, and you can also come straight here if you know
              what you are looking for.
            </p>
          </div>

          <div className="gpj-rails">
            <button type="button" className="gpj-rail" onClick={() => setCurrentView('forms')}>
              <span className="gpj-rail__kicker">Forms library</span>
              <h3>All Ontario family court forms</h3>
              <p>
                The official forms, organised by what you are trying to do rather than by
                number alone, with plain-language notes for verified entries.
              </p>
              <div className="gpj-rail__meta">
                <span className="gpj-chip">Search by form number</span>
                <span className="gpj-chip">Search by situation</span>
                <span className="gpj-chip">Fee waiver forms</span>
              </div>
              <span className="gpj-rail__go">Browse the forms library →</span>
            </button>

            <button type="button" className="gpj-rail" onClick={() => setCurrentView('resources')}>
              <span className="gpj-rail__kicker">Legal library</span>
              <h3>Case law and legal resources</h3>
              <p>
                Plain-language resources about the legislation and decisions that shape family
                cases in Ontario, with primary-source links where available.
              </p>
              <div className="gpj-rail__meta">
                <span className="gpj-chip">Family Law Rules</span>
                <span className="gpj-chip">Children's Law Reform Act</span>
                <span className="gpj-chip">Divorce Act</span>
                <span className="gpj-chip">Case summaries</span>
              </div>
              <span className="gpj-rail__go">Browse the legal library →</span>
            </button>
          </div>

          <div className="gpj-minor-rails">
            <button type="button" className="gpj-minor" onClick={() => setCurrentView('court-guide')}>
              <span>
                <span className="gpj-minor__title">Court guide</span>
                <span className="gpj-minor__sub">What happens on the day, and what to bring</span>
              </span>
              <span className="gpj-minor__arrow" aria-hidden="true">→</span>
            </button>
            <button type="button" className="gpj-minor" onClick={() => setCurrentView('directory')}>
              <span>
                <span className="gpj-minor__title">Find Help directory</span>
                <span className="gpj-minor__sub">Family law professionals and support services in Ontario</span>
              </span>
              <span className="gpj-minor__arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </div>
      </section>

      <section className="gpj-section">
        <div className="gpj-wrap">
          <div className="gpj-safety">
            <div>
              <h3>If you are not safe</h3>
              <p>
                Safety planning comes before paperwork. If you are leaving an abusive
                situation or are afraid for your safety or your children's, find support now.
              </p>
            </div>
            <div className="gpj-safety__actions">
              <button type="button" className="gpj-btn gpj-btn--teal" onClick={() => setCurrentView('directory')}>
                Find safety support
              </button>
              <button
                type="button"
                className="gpj-btn gpj-btn--outline-teal"
                onClick={() => setCurrentView('access-selection')}
              >
                Referral / free access
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
